import { useReducedMotion } from "framer-motion";
import type { Transition, Variants } from "framer-motion";

/**
 * Costanti di movimento condivise del design system v2 "Palazzo" (§4).
 * Una sola curva, tre durate. Ogni hook rispetta `prefers-reduced-motion`.
 */

/** L'unica curva ammessa dal sistema. */
export const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** Le uniche durate ammesse, in secondi (120 / 240 / 420 ms). */
export const DUR = {
  /** 120ms — feedback immediato: hover, press. */
  fast: 0.12,
  /** 240ms — transizioni di stato, entrate. */
  base: 0.24,
  /** 420ms — transizioni di pagina, disegno grafici. */
  slow: 0.42,
} as const;

/** Ritardo fra elementi in una lista con stagger (40ms) e tetto massimo (400ms). */
export const STAGGER_STEP = 0.04;
export const STAGGER_MAX = 0.4;

/** Transizioni pronte all'uso, coerenti con EASE + DUR. */
export const TRANSITION: Record<keyof typeof DUR, Transition> = {
  fast: { duration: DUR.fast, ease: EASE },
  base: { duration: DUR.base, ease: EASE },
  slow: { duration: DUR.slow, ease: EASE },
};

/**
 * Durate in millisecondi (per calcoli JS/`setTimeout`).
 * In CSS usare SEMPRE i token: `duration-fast|base|slow` + `ease-palazzo`.
 * Le varianti arbitrarie del tipo `duration-<parentesi>240ms<parentesi>` NON
 * funzionano qui: con tailwindcss-animate sono ambigue (transition + animation)
 * e Tailwind non genera alcuna regola.
 */
export const DUR_MS = {
  fast: Math.round(DUR.fast * 1000),
  base: Math.round(DUR.base * 1000),
  slow: Math.round(DUR.slow * 1000),
} as const;

/* ------------------------------------------------------------------ *
 * Varianti statiche — usarle direttamente solo se il movimento ridotto
 * è già gestito altrove. Altrimenti preferire gli hook `use*` sotto.
 * ------------------------------------------------------------------ */

/** Entrata canonica: opacità + 8px verso l'alto (§4). */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: TRANSITION.base },
};

/** Degradazione per `prefers-reduced-motion`: solo dissolvenza. */
export const fadeOnly: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: TRANSITION.base },
};

/** Contenitore di lista: orchestra i figli a 40ms l'uno dall'altro. */
export const staggerList: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: STAGGER_STEP, delayChildren: 0.02 },
  },
};

/** Transizione di pagina: entrata 420ms, uscita 240ms. */
export const pageTransition: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: TRANSITION.slow },
  exit: { opacity: 0, y: -6, transition: TRANSITION.base },
};

const pageTransitionReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: TRANSITION.base },
  exit: { opacity: 0, transition: TRANSITION.fast },
};

const staggerListReduced: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0, delayChildren: 0 } },
};

/* ------------------------------------------------------------------ *
 * Hook — sempre questi nelle pagine: degradano a fade se l'utente
 * ha chiesto meno movimento.
 * ------------------------------------------------------------------ */

/** True se l'utente ha chiesto movimento ridotto (mai `null`). */
export function useReducedMotionSafe(): boolean {
  return useReducedMotion() === true;
}

/** Varianti d'entrata (fade + 8px), degradate a solo fade se serve. */
export function useFadeUp(): Variants {
  return useReducedMotionSafe() ? fadeOnly : fadeUp;
}

/**
 * Come `useFadeUp` ma con il ritardo di stagger già dentro la variante:
 * serve quando l'elemento entra da solo, senza un contenitore che orchestri.
 */
export function useFadeUpAt(index = 0): Variants {
  const reduced = useReducedMotionSafe();
  const delay = reduced ? 0 : staggerDelay(index);
  return {
    hidden: reduced ? { opacity: 0 } : { opacity: 0, y: 8 },
    visible: {
      opacity: 1,
      ...(reduced ? {} : { y: 0 }),
      transition: { ...TRANSITION.base, delay },
    },
  };
}

/** Varianti del contenitore di lista con stagger, annullato se serve. */
export function useStaggerList(): Variants {
  return useReducedMotionSafe() ? staggerListReduced : staggerList;
}

/** Varianti di transizione di pagina, degradate a solo fade se serve. */
export function usePageTransition(): Variants {
  return useReducedMotionSafe() ? pageTransitionReduced : pageTransition;
}

/** Ritardo d'ingresso per l'elemento `index` di una lista (capped a 400ms). */
export function staggerDelay(index: number): number {
  return Math.min(Math.max(index, 0) * STAGGER_STEP, STAGGER_MAX);
}

/** Come `staggerDelay`, ma azzerato con movimento ridotto. */
export function useStaggerDelay(index: number): number {
  return useReducedMotionSafe() ? 0 : staggerDelay(index);
}
